/**
 * Web page block (spec §8.16). Live: a sandboxed frame on the web origin browsing through the
 * recording proxy; in-frame navigation moves only this reader's address bar (Q26), the address bar and
 * **Use this page** commit the shared URL. Freeze: the frame serialises its DOM, the server builds a
 * WACZ from the session's recording (§11.8), and the snapshot is written to history and `frozen` in
 * one commit. Frozen: a replay frame on the web origin is handed the WACZ as a Blob (OPFS when pinned,
 * else the asset endpoint) and replays it with ReplayWeb.page, offline too.
 */
import { LoroMap } from "loro-crdt";
import type { SnapshotRef } from "@kss/schema";
import { ArrowLeft, ArrowRight, History, RotateCw, Snowflake } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, unwrap } from "../../api/client";
import { webOrigin } from "../../lib/webOrigin";
import { getBlob } from "../../offline/opfsBlobCache";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { bodyMap, LoroTextInput } from "../textField";
import { addSnapshot, commitUrl, liveMessage, normalizeUrl, readWebPage, sameSnapshot, setFrozen, waybackTs, type WebPageState } from "./model";
import { readHeight } from "../height";
import { usePopover } from "../../ui/usePopover";
import { useT, type Translate } from "../../i18n";

const SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups";
const SNAPSHOT_ANSWER_MS = 15_000;

interface Session {
  token: string;
  prefix: string;
  webOrigin: string;
  expiresAt: string;
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

const sameState = (a: WebPageState, b: WebPageState) => JSON.stringify(a) === JSON.stringify(b);

export function WebPageBlock({ handle, treeId, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const state = useLoroSelector(handle.doc, () => (body ? readWebPage(body) : null), (a, b) => !!a && !!b && sameState(a, b));
  // [M21] the generic grip of §8.6 item 8 replaced this block's own handle: when the node carries a
  // height, the chrome sizes the block and the frame fills it; `body.height` is the legacy value of
  // pages authored before M21 (§7.4.12) and is still read, never written.
  const resized = useLoroSelector(handle.doc, () => readHeight(node) !== null) ?? false;
  const online = useOnline();
  // the snapshot list dismisses like every other menu (§8.6 item 5)
  const { open: historyOpen, toggle: toggleHistory, close: closeHistory, buttonRef: historyButton, panelRef: historyPanel } = usePopover<HTMLUListElement>();
  if (!body || !state) return null;
  const frameHeight = resized ? undefined : state.height;

  return (
    <div className="rounded-md border border-neutral-200 bg-[var(--surface)] shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="webpage-block" data-frozen={state.frozen ? "true" : "false"} data-fill-column onFocus={onFocus}>
      {state.frozen ? (
        <FrozenView key={`${state.frozen.asset.hash}:${state.frozen.capturedAt}`} snapshot={state.frozen} height={frameHeight} readOnly={readOnly} online={online} onUnfreeze={() => setFrozen(handle.doc, body, null)} />
      ) : (
        <LiveView handle={handle} treeId={treeId} body={body} state={state} height={frameHeight} readOnly={readOnly} online={online} />
      )}
      <div className="flex items-center gap-2 border-t border-neutral-200 px-2 py-1 text-xs dark:border-neutral-800">
        <LoroTextInput handle={handle} map={body} field="caption" placeholder={readOnly ? "" : t("media.caption")} readOnly={readOnly} testId="webpage-caption" />
        {state.snapshots.length > 0 && (
          <div className="relative shrink-0">
            <button ref={historyButton} className="flex items-center gap-1 rounded px-1 py-0.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-haspopup="menu" aria-expanded={historyOpen} onClick={toggleHistory} data-testid="webpage-history-button">
              <History size={13} /> {t("web.snapshots", { count: state.snapshots.length })}
            </button>
            {historyOpen && (
              <ul ref={historyPanel} className="absolute bottom-7 right-0 z-30 w-80 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 shadow-[var(--shadow-lg)] dark:border-neutral-700" data-menu-open="" data-testid="webpage-history">
                {[...state.snapshots].reverse().map(({ id, ref }) => (
                  <li key={id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-neutral-50 dark:hover:bg-neutral-800" data-testid="webpage-snapshot">
                    <span className="grow truncate" title={ref.pageUrl}>
                      {new Date(ref.capturedAt).toLocaleString()} · {ref.title || ref.pageUrl}
                    </span>
                    {sameSnapshot(state.frozen, ref) ? (
                      <span className="text-neutral-500">{t("web.shown")}</span>
                    ) : (
                      !readOnly && (
                        <button
                          className="underline"
                          onClick={() => {
                            setFrozen(handle.doc, body, ref);
                            closeHistory();
                          }}
                          data-testid="webpage-show-snapshot"
                        >
                          {t("web.showSnapshot")}
                        </button>
                      )
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LiveView({ handle, treeId, body, state, height, readOnly, online }: { handle: BlockComponentProps["handle"]; treeId: string; body: LoroMap; state: WebPageState; height: number | undefined; readOnly: boolean; online: boolean }) {
  const t = useT();
  const frame = useRef<HTMLIFrameElement>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{ url: string; title: string } | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [freezing, setFreezing] = useState(false);
  const pending = useRef(new Map<number, (m: { url: string; title: string; dom: string }) => void>());
  const nextId = useRef(1);
  const origin = session?.webOrigin ?? webOrigin();

  // a token for this block while it is shown (renewed on demand; the recording session stays)
  const requestSession = useCallback(async (): Promise<Session> => unwrap<Session>(await api.POST("/api/web/sessions", { body: { docId: handle.crdtDocId, treeId } })), [handle.crdtDocId, treeId]);
  const sessionError = useCallback((e: unknown) => (e instanceof ApiError && e.status === 403 ? e.detail : t("web.cannotOpen")), [t]);
  const openSession = async (): Promise<Session | null> => {
    try {
      const s = await requestSession();
      setSession(s);
      return s;
    } catch (e) {
      setError(sessionError(e));
      return null;
    }
  };

  useEffect(() => {
    if (!online || !state.url) return;
    let cancelled = false;
    requestSession().then(
      (s) => !cancelled && (setSession(s), setError(null)),
      (e: unknown) => !cancelled && setError(sessionError(e)),
    );
    return () => {
      cancelled = true;
    };
  }, [online, state.url, requestSession, sessionError]);

  // Navigate the frame when the committed URL changes — first load, the address bar, another reader's
  // commit — unless this reader is already on that page. Guarded by the last loaded URL: a renewed token
  // (Freeze renews it) re-runs the effect but must not send the reader back from wherever they navigated.
  const loadedUrl = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !state.url || !frame.current) return;
    if (loadedUrl.current === state.url) return;
    const already = loadedUrl.current !== null && location !== null && normalizeUrl(location.url) === state.url;
    loadedUrl.current = state.url;
    if (!already) frame.current.src = session.prefix + state.url;
    // `location` is read, not tracked: in-frame navigation alone never reloads the frame
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, state.url]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const m = liveMessage(event, origin, frame.current?.contentWindow);
      if (!m) return;
      if (m.type === "kss:location") setLocation({ url: m.url, title: m.title });
      else pending.current.get(m.id)?.(m);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin]);

  const nav = (action: "back" | "forward" | "reload") => frame.current?.contentWindow?.postMessage({ type: "kss:nav", action }, origin);

  const commit = (value: string) => {
    if (readOnly) return;
    const url = normalizeUrl(value);
    if (!url) {
      setError(t("web.badAddress"));
      return;
    }
    setError(null);
    commitUrl(handle.doc, body, url);
    setAddress(null);
    if (url === state.url && session && frame.current) frame.current.src = session.prefix + url; // same URL: go there again
  };

  const freeze = async () => {
    const win = frame.current?.contentWindow;
    if (!win || freezing) return;
    setFreezing(true);
    setError(null);
    try {
      const s = (await openSession()) ?? session;
      if (!s) throw new Error(t("web.cannotOpen"));
      const id = nextId.current++;
      const answer = await new Promise<{ url: string; title: string; dom: string }>((resolve, reject) => {
        const timer = setTimeout(() => {
          pending.current.delete(id);
          reject(new Error(t("web.noAnswer")));
        }, SNAPSHOT_ANSWER_MS);
        pending.current.set(id, (m) => {
          clearTimeout(timer);
          pending.current.delete(id);
          resolve(m);
        });
        win.postMessage({ type: "kss:snapshot", id }, origin);
      });
      const res = await api.POST("/api/web/snapshots", { body: { token: s.token, pageUrl: answer.url, title: answer.title, dom: answer.dom } });
      let ref: SnapshotRef;
      if (res.response.status === 202) ref = await pollSnapshot((res.data as { jobId: string }).jobId, t);
      else ref = unwrap<SnapshotRef>(res);
      addSnapshot(handle.doc, body, ref);
    } catch (e) {
      setError(e instanceof ApiError ? t("web.freezeFailedWith", { error: e.detail }) : e instanceof Error ? e.message : t("web.freezeFailed"));
    } finally {
      setFreezing(false);
    }
  };

  const shown = location?.url ?? state.url;
  const differs = !!location && normalizeUrl(location.url) !== state.url;

  if (!online) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-sm text-neutral-500" style={{ height: Math.min(height ?? state.height, 240) }} data-testid="webpage-offline">
        <span className="max-w-full truncate">{state.url || t("web.noAddress")}</span>
        <span>{t("web.offline")}</span>
      </div>
    );
  }

  return (
    <div data-fill-column>
      <div className="flex items-center gap-1 border-b border-neutral-200 px-1 py-1 dark:border-neutral-800">
        <IconButton label={t("web.back")} onClick={() => nav("back")} testId="webpage-back">
          <ArrowLeft size={14} />
        </IconButton>
        <IconButton label={t("web.forward")} onClick={() => nav("forward")} testId="webpage-forward">
          <ArrowRight size={14} />
        </IconButton>
        <IconButton label={t("web.reload")} onClick={() => nav("reload")} testId="webpage-reload">
          <RotateCw size={14} />
        </IconButton>
        <input
          className="min-w-0 grow rounded border border-neutral-300 bg-transparent px-2 py-0.5 text-sm dark:border-neutral-700"
          value={address ?? shown}
          placeholder={readOnly ? "" : "https://…"}
          readOnly={readOnly}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(address ?? shown);
            if (e.key === "Escape") setAddress(null);
          }}
          data-testid="webpage-address"
          data-committed={state.url}
          title={location?.title ?? ""}
        />
        {!readOnly && differs && (
          <button className="shrink-0 rounded px-2 py-0.5 text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => commit(location!.url)} data-testid="webpage-use-page">
            {t("web.usePage")}
          </button>
        )}
        {!readOnly && state.url && (
          <button
            className="flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-800"
            onClick={() => void freeze()}
            disabled={freezing || !session}
            data-testid="webpage-freeze"
            data-state={freezing ? "running" : "idle"}
            title={t("web.freezeTitle")}
          >
            <Snowflake size={14} /> {freezing ? t("web.freezing") : t("web.freeze")}
          </button>
        )}
      </div>
      {error && (
        <p className="px-2 py-1 text-xs text-red-700 dark:text-red-400" data-testid="webpage-error">
          {error}
        </p>
      )}
      {state.url ? (
        <iframe ref={frame} title={location?.title || state.url} sandbox={SANDBOX} className="block w-full bg-white" style={{ height }} data-fill-surface data-testid="webpage-live-frame" />
      ) : (
        <div className="p-6 text-sm text-neutral-500" data-testid="webpage-empty">
          {readOnly ? t("web.empty") : t("web.emptyEdit")}
        </div>
      )}
    </div>
  );
}

async function pollSnapshot(jobId: string, t: Translate): Promise<SnapshotRef> {
  for (;;) {
    await new Promise((r) => setTimeout(r, 2000));
    const job = unwrap<{ state: string; snapshot?: SnapshotRef | null; error?: string | null }>(await api.GET("/api/web/snapshots/jobs/{job_id}", { params: { path: { job_id: jobId } } }));
    if (job.state === "done" && job.snapshot) return job.snapshot;
    if (job.state === "failed") throw new Error(job.error ? t("web.freezeFailedWith", { error: job.error }) : t("web.freezeFailed"));
  }
}

function FrozenView({ snapshot, height, readOnly, online, onUnfreeze }: { snapshot: SnapshotRef; height: number | undefined; readOnly: boolean; online: boolean; onUnfreeze(): void }) {
  const t = useT();
  const frame = useRef<HTMLIFrameElement>(null);
  const origin = webOrigin();
  const [original, setOriginal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [src] = useState(() => `${origin}/replay/?parents=${encodeURIComponent(window.location.origin)}`);

  useEffect(() => {
    let blob: Blob | null = null;
    let cancelled = false;
    const load = async () => {
      blob = await getBlob(snapshot.asset.hash).catch(() => null);
      if (!blob) {
        try {
          const r = await fetch(`/api/assets/${snapshot.asset.hash}`, { credentials: "same-origin" });
          if (!r.ok) throw new Error(String(r.status));
          blob = await r.blob();
        } catch {
          if (!cancelled) setError("web.snapshotOffline"); // a key: translated where it is shown
          return;
        }
      }
      send();
    };
    const send = () => {
      const win = frame.current?.contentWindow;
      if (!win || !blob) return;
      const ts = waybackTs(original && snapshot.recordedAt ? snapshot.recordedAt : snapshot.capturedAt);
      win.postMessage({ type: "kss:replay", blob, url: snapshot.pageUrl, ts }, origin);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== frame.current?.contentWindow) return;
      if ((event.data as { type?: string } | null)?.type === "kss:replay-ready") send();
    };
    window.addEventListener("message", onMessage);
    void load();
    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
    };
  }, [snapshot, original, origin, online]);

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-neutral-200 px-2 py-1 text-xs dark:border-neutral-800">
        <Snowflake size={14} className="text-sky-600" />
        <span className="min-w-0 grow truncate" data-testid="webpage-frozen-url" title={snapshot.pageUrl}>
          {snapshot.title || snapshot.pageUrl} · {t("web.frozenAt", { date: new Date(snapshot.capturedAt).toLocaleString() })}
        </span>
        {snapshot.recordedAt && (
          <label className="flex shrink-0 items-center gap-1" title={t("web.replayTitle")}>
            <input type="checkbox" checked={original} onChange={(e) => setOriginal(e.target.checked)} data-testid="webpage-replay-original" /> {t("web.replay")}
          </label>
        )}
        {!readOnly && (
          <button className="shrink-0 rounded px-2 py-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={onUnfreeze} data-testid="webpage-unfreeze">
            {t("web.unfreeze")}
          </button>
        )}
      </div>
      {error ? (
        <div className="p-6 text-sm text-neutral-500" data-testid="webpage-frozen-error" style={{ height: Math.min(height ?? 600, 240) }}>
          {t(error)}
        </div>
      ) : (
        <iframe key={original ? "original" : "dom"} ref={frame} src={src} title={snapshot.title || snapshot.pageUrl} sandbox={SANDBOX} className="block w-full bg-white" style={{ height }} data-fill-surface data-testid="webpage-replay-frame" />
      )}
    </div>
  );
}

function IconButton({ label, onClick, testId, children }: { label: string; onClick(): void; testId: string; children: React.ReactNode }) {
  return (
    <button className="rounded p-1 text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800" aria-label={label} title={label} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}
